import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { SeoLayout } from '@/components/astro/new-design-25/SEO/seo-layout';
import { seo } from '@/seo/seo';

const heroTitle = 'PhD Thesis Research Assistant';
const heroDescription =
  'PhD thesis writing assistant with audio review. Listen to your drafts to catch errors, improve flow, and review advisor feedback. Perfect for dissertations.';

const featureTitle =
  'Strategic Positioning: "The Thesis Audio Review Revolution"';
const featureSubtitle = '"Audio Thesis Review"';
const featureDescription =
  'We are not another Grammarly or Scrivener - we are the audio thesis review solution that helps:';
const featureBadge = 'Thesis Focus';
const featureVariant = 'default';
const featureCtaText = 'Start Audio Review';

const featureFeatures = [
  {
    title: 'Catch Flow Issues',
    description: 'PhD students catch flow issues by listening to their writing',
  },
  {
    title: 'Audio Advisor Feedback',
    description: 'Doctoral candidates review advisor feedback through audio',
  },
  {
    title: 'Identify Repetitive Patterns',
    description:
      'Thesis writers identify repetitive language patterns through listening',
  },
  {
    title: 'Efficient Proofreading',
    description: 'Graduate students proofread 200+ page documents efficiently',
  },
];

const comparisonTableData = {
  title: 'Comparison Table',
  description:
    'Compare thesis writing and review tools for PhD students and doctoral candidates.',
  columns: [
    {
      header: 'Listening',
      subheader: 'Audio Thesis Tool',
      isHighlighted: true,
    },
    { header: 'Grammarly', isHighlighted: false },
    { header: 'Scrivener', isHighlighted: false },
    { header: 'Word', subheader: 'Comments', isHighlighted: false },
  ],
  rows: [
    {
      feature: 'Flow Detection',
      description:
        'Ability to identify awkward phrasing and improve writing flow.',
      values: [
        {
          text: 'Identifies awkward phrasing through audio',
          icon: 'check' as const,
        },
        { text: 'Grammar only', icon: 'close' as const },
        { text: 'Organization tool only', icon: 'close' as const },
        { text: 'Manual review needed', icon: 'close' as const },
      ],
    },
    {
      feature: 'Large Document Review',
      description:
        'Support for reviewing lengthy thesis and dissertation documents.',
      values: [
        { text: 'Efficient audio scanning', icon: 'check' as const },
        { text: 'Slow for 200+ pages', icon: 'warning' as const },
        { text: 'Good organization', icon: 'warning' as const },
        { text: 'Tedious scrolling', icon: 'close' as const },
      ],
    },
    {
      feature: 'Advisor Feedback',
      description:
        'Integration and review of advisor comments and suggestions.',
      values: [
        { text: 'Converts to organized audio', icon: 'check' as const },
        { text: 'No feedback integration', icon: 'close' as const },
        { text: 'Basic collaboration', icon: 'warning' as const },
        { text: 'Comments system', icon: 'warning' as const },
      ],
    },
    {
      feature: 'Structural Issues',
      description:
        'Detection of structural problems and organizational weaknesses.',
      values: [
        { text: 'Reveals through listening', icon: 'check' as const },
        { text: 'Misses flow problems', icon: 'close' as const },
        { text: 'No content analysis', icon: 'close' as const },
        { text: 'Manual identification', icon: 'close' as const },
      ],
    },
    {
      feature: 'Academic Focus',
      description:
        'Specialized features for academic writing and dissertation needs.',
      values: [
        { text: 'Designed for dissertations', icon: 'check' as const },
        { text: 'General writing focus', icon: 'warning' as const },
        { text: 'Academic writing support', icon: 'warning' as const },
        { text: 'Basic academic features', icon: 'warning' as const },
      ],
    },
  ],
  showIcons: true,
  ariaLabel: 'PhD Thesis Research Assistant Comparison Table',
};

const seoSections = [
  {
    title: "Why Listening to Your Thesis Reveals Problems Reading Can't Catch",
    content:
      'Professional writers have long known that reading your work aloud reveals problems that silent reading misses - awkward phrasing, run-on sentences, and unclear transitions become obvious when heard rather than read. For PhD students writing 200+ page dissertations, this principle becomes crucial but practically impossible to implement manually. Our audio thesis review system lets you listen to your entire dissertation with natural-sounding voices, revealing flow issues, repetitive language patterns, and unclear arguments that are invisible during silent editing.',
    contentSecondary:
      'The audio review process is particularly powerful for identifying problems common in academic writing: overly complex sentence structures, unclear pronoun references, and logical gaps between paragraphs. When you hear your methodology section read aloud, you immediately notice where explanations become convoluted or where transitions feel jarring. This auditory editing approach complements traditional writing tools like Grammarly by catching higher-level structural issues that grammar checkers cannot identify, making it an essential tool for thesis quality improvement.',
  },
  {
    title: 'Transform Advisor Feedback into Actionable Audio Guidance',
    content:
      'Advisor comments on thesis drafts often come as marginal notes, tracked changes, and email feedback that requires constant switching between documents and interfaces. Our thesis writing assistant converts all forms of feedback into organized audio content, allowing you to listen to advisor suggestions while simultaneously reviewing the relevant sections of your thesis. This approach eliminates the cognitive overhead of managing multiple feedback sources and helps you understand suggested revisions in context.',
    contentSecondary:
      "The system also enables efficient proofreading of revised sections by reading updated chapters aloud, helping you ensure that changes maintain narrative flow and argumentative coherence. PhD students consistently report catching more errors and producing higher-quality revisions when using audio review compared to traditional silent proofreading methods. Whether you're incorporating committee feedback, revising for defense preparation, or conducting final proofreading, audio thesis review transforms the editing process from tedious text scanning into engaging content improvement.",
  },
];

export const Route = createFileRoute(
  '/_marketing/_seo/phd-thesis-research-assistant',
)({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title:
          'PhD Thesis Research Assistant - Audio Review for Dissertations | Listening',
        description:
          'PhD thesis writing assistant with audio review. Listen to your drafts to catch errors, improve flow, and review advisor feedback. Perfect for dissertations.',
      }),
    ],
  }),
  component: () => (
    <SeoLayout
      comparisonTableData={comparisonTableData}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      pageDescription={heroDescription}
      pageTitle={heroTitle}
      seoSections={seoSections}
    />
  ),
});
